import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/lib/i18n';
import {
  LayoutDashboard, MessageSquare, Clock, CheckCircle2, Users, AlertTriangle,
  BookOpen, ArrowRight
} from 'lucide-react';

export default function TenantDashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/tenants/${user?.tenant_id}/dashboard`).then(r => setData(r.data)).catch(() => {});
  }, [user?.tenant_id]);

  if (!data) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-7 h-7 border-2 border-[#EF694C]/30 border-t-[#EF694C] rounded-full animate-spin" />
    </div>
  );

  const stats = [
    { label: t.open, value: data.open_conversations || 0, icon: MessageSquare, color: '#EF694C', desc: t.needAttention },
    { label: t.pending, value: data.pending_conversations || 0, icon: Clock, color: '#F59E0B', desc: t.waitingOnCustomer },
    { label: t.resolved, value: data.resolved_conversations || 0, icon: CheckCircle2, color: '#10B981', desc: t.thisPeriod },
    { label: t.teamMembers, value: data.team_size || 0, icon: Users, color: '#3B82F6', desc: t.activeAgents },
  ];

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1200px] mx-auto" data-testid="tenant-dashboard">
      {/* Header */}
      <div className="animate-fade-in-up">
        <h2 className="font-heading font-bold text-2xl text-white">
          {typeof t.welcomeBackName === 'function' ? t.welcomeBackName(user?.name?.split(' ')[0]) : t.dashboard}
        </h2>
        <p className="text-sm text-[#a1a1aa] mt-1">{t.dashboardSubtitle}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in-up stagger-1">
        {stats.map(s => (
          <div key={s.label} className="bg-[#121214] border border-white/10 rounded-xl p-5 hover:border-white/15 transition-all">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${s.color}15` }}>
                <s.icon className="w-5 h-5" style={{ color: s.color }} />
              </div>
            </div>
            <p className="text-2xl font-bold text-white">{s.value}</p>
            <p className="text-sm text-white mt-0.5">{s.label}</p>
            <p className="text-xs text-[#71717a] mt-0.5">{s.desc}</p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-4 animate-fade-in-up stagger-2">
        {/* Recent conversations */}
        <div className="bg-[#121214] border border-white/10 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-sm text-white">{t.recentConversations}</h3>
            <button onClick={() => navigate('/inbox')} className="text-xs text-[#EF694C] hover:underline flex items-center gap-1">
              {t.openInbox} <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          {data.recent_conversations?.length ? (
            <div className="space-y-2">
              {data.recent_conversations.slice(0, 5).map(c => (
                <div key={c.id} className="flex items-center justify-between p-3 bg-[#09090b] rounded-lg border border-white/5">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white truncate">{c.customer_name || c.subject || 'Konversation'}</p>
                    <p className="text-xs text-[#71717a]">{c.channel || 'web'}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    c.status === 'open' ? 'text-[#EF694C] bg-[#EF694C]/10' :
                    c.status === 'pending' ? 'text-[#F59E0B] bg-[#F59E0B]/10' :
                    'text-[#10B981] bg-[#10B981]/10'
                  }`}>{c.status}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#52525b] py-6 text-center">{t.noConversations}</p>
          )}
        </div>

        {/* KB Overview */}
        <div className="bg-[#121214] border border-white/10 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-sm text-white">{t.knowledgeBase}</h3>
            <button onClick={() => navigate('/knowledge')} className="text-xs text-[#EF694C] hover:underline flex items-center gap-1">
              {t.manageKB} <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#09090b] rounded-lg p-4 border border-white/5 text-center">
              <BookOpen className="w-6 h-6 text-[#3B82F6] mx-auto mb-2" />
              <p className="text-xl font-bold text-white">{data.kb_sources || 0}</p>
              <p className="text-xs text-[#71717a]">{t.indexedSources}</p>
            </div>
            <div className="bg-[#09090b] rounded-lg p-4 border border-white/5 text-center">
              <MessageSquare className="w-6 h-6 text-[#10B981] mx-auto mb-2" />
              <p className="text-xl font-bold text-white">{(data.open_conversations || 0) + (data.pending_conversations || 0)}</p>
              <p className="text-xs text-[#71717a]">{t.open}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
